// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConsentPreferences
 * @package @geenius/analytics/react-css
 * @description Renders granular consent category controls for the React CSS
 * analytics variant.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { Button } from "@geenius/ui/react-css";
import type React from "react";
import { useId, useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { useAnalyticsConsent } from "../provider";

export type ConsentPreferenceScope =
  | "analytics"
  | "marketing"
  | "personalization";

export type ConsentPreferenceValues = Pick<
  ConsentSettings,
  ConsentPreferenceScope
>;

const CONSENT_PREFERENCE_OPTIONS = [
  ["personalization", "analytics.consent.personalization"],
  ["analytics", "analytics.consent.analyticsScope"],
  ["marketing", "analytics.consent.marketing"],
] as const satisfies readonly [ConsentPreferenceScope, string][];

export const DEFAULT_CONSENT_PREFERENCES: ConsentPreferenceValues = {
  analytics: false,
  marketing: false,
  personalization: false,
};

export function buildConsentSettings(
  preferences: ConsentPreferenceValues,
): ConsentSettings {
  return {
    essential: true,
    ...preferences,
    timestamp: Date.now(),
  };
}

function normalizeConsentPreferences(
  preferences?: Partial<ConsentPreferenceValues> | null,
): ConsentPreferenceValues {
  return {
    ...DEFAULT_CONSENT_PREFERENCES,
    ...preferences,
  };
}

/**
 * Props for rendering the granular React CSS consent preferences control.
 *
 * @property value Optional controlled preference values.
 * @property onChange Called whenever a category toggle changes.
 * @property onSave Called when the user saves the current category choices.
 * @property showActions Whether to show the built-in save button.
 * @property className Optional class name merged into the fieldset.
 */
export interface ConsentPreferencesProps {
  value?: Partial<ConsentPreferenceValues>;
  onChange?: (preferences: ConsentPreferenceValues) => void;
  onSave?: (settings: ConsentSettings) => void;
  showActions?: boolean;
  className?: string;
}

/**
 * Renders category-level consent controls for analytics, marketing, and
 * personalization scopes.
 *
 * @param props Controlled values and callbacks for the consent controls.
 * @returns A fieldset containing category toggles and an optional save action.
 */
export function ConsentPreferences(
  props: ConsentPreferencesProps,
): React.ReactElement {
  const copy = useAnalyticsCopy();
  const consent = useAnalyticsConsent();
  const legendId = useId();
  const [localPreferences, setLocalPreferences] =
    useState<ConsentPreferenceValues>(() =>
      normalizeConsentPreferences(consent?.consentSettings),
    );
  const preferences =
    props.value !== undefined
      ? normalizeConsentPreferences(props.value)
      : localPreferences;

  const updatePreference =
    (scope: ConsentPreferenceScope) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const next = {
        ...preferences,
        [scope]: event.currentTarget.checked,
      };
      if (props.value === undefined) {
        setLocalPreferences(next);
      }
      props.onChange?.(next);
    };

  const savePreferences = (): void => {
    const settings = buildConsentSettings(preferences);
    consent?.updateConsent(settings);
    props.onSave?.(settings);
  };

  return (
    <fieldset
      className={cx("gn-analytics-consent-preferences", props.className)}
      data-analytics="consent-preferences"
      aria-labelledby={legendId}
    >
      <legend
        id={legendId}
        className="gn-analytics-consent-preferences__legend"
      >
        {copy.t("analytics.consent.preferencesLegend")}
      </legend>
      {CONSENT_PREFERENCE_OPTIONS.map(([scope, labelKey]) => (
        <label key={scope} className="gn-analytics-consent-preferences__option">
          <span className="gn-analytics-consent-preferences__label">
            {copy.t(labelKey)}
          </span>
          <input
            type="checkbox"
            className="gn-analytics-consent-preferences__control"
            checked={preferences[scope]}
            onChange={updatePreference(scope)}
          />
        </label>
      ))}
      {props.showActions !== false && (
        <div className="gn-analytics-consent-preferences__actions">
          <Button
            type="button"
            variant="secondary"
            data-analytics="consent-save"
            onClick={savePreferences}
          >
            {copy.t("analytics.consent.savePreferences")}
          </Button>
        </div>
      )}
    </fieldset>
  );
}
